import fixWebmDuration from 'fix-webm-duration';
import { ArrayBufferTarget, Muxer } from 'webm-muxer';

export interface WebCodecsRecorderOptions {
  fps?: number;
  bitsPerSecond?: number;
  /** One of 'vp9', 'vp8', 'av1'. Defaults to 'vp9'. */
  codec?: 'vp9' | 'vp8' | 'av1';
  /** GOP size (distance between keyframes). Defaults to 2s of video. */
  keyFrameInterval?: number;
}

const CODEC_STRINGS: Record<NonNullable<WebCodecsRecorderOptions['codec']>, string> = {
  vp9: 'vp09.00.10.08',
  vp8: 'vp8',
  av1: 'av01.0.04M.08',
};

const MATROSKA_CODECS: Record<NonNullable<WebCodecsRecorderOptions['codec']>, string> = {
  vp9: 'V_VP9',
  vp8: 'V_VP8',
  av1: 'V_AV1',
};

/**
 * A MediaRecorder-compatible recorder built on top of the WebCodecs API
 * and `webm-muxer`. Produces a **seekable** WebM (with Cues, duration and
 * per-cluster timecodes) so `video.currentTime = X` works cleanly in the
 * viewer. MediaRecorder's native WebM output does not include SeekHead /
 * duration metadata and is why our viewer's thumbnail/scrub logic had
 * to work around it.
 *
 * Usage mirrors the old `Recorder` class:
 *   const rec = new WebCodecsRecorder(canvas, { fps: 60 });
 *   rec.start();                  // after canvas is sized
 *   rec.captureFrame();           // once per source video frame (rVFC)
 *   const blob = await rec.stop();
 */
export class WebCodecsRecorder {
  private encoder: VideoEncoder | null = null;
  private muxer: Muxer<ArrayBufferTarget> | null = null;
  private running = false;
  private startedAt = 0;
  /**
   * Wallclock (performance.now()) time when the very first VideoFrame was
   * encoded. This is the instant the WebM's `t=0` corresponds to — the
   * muxer is configured with `firstTimestampBehavior: 'offset'`, which
   * shifts the first frame's timestamp to zero. Callers (e.g. pose data
   * encoding) must use THIS value, not `startedAt`, as the time base for
   * anything that needs to align with video playback.
   */
  private firstFrameWallclock: number | null = null;
  private lastFrameWallclock: number | null = null;
  private frameCount = 0;
  private keyframeEvery: number;
  private width = 0;
  private height = 0;
  private readonly codecKey: NonNullable<WebCodecsRecorderOptions['codec']>;

  static isSupported(): boolean {
    return (
      typeof VideoEncoder !== 'undefined' &&
      typeof VideoFrame !== 'undefined' &&
      typeof EncodedVideoChunk !== 'undefined'
    );
  }

  static async isCodecSupported(
    codec: NonNullable<WebCodecsRecorderOptions['codec']>,
  ): Promise<boolean> {
    if (!WebCodecsRecorder.isSupported()) return false;
    try {
      const res = await VideoEncoder.isConfigSupported({
        codec: CODEC_STRINGS[codec],
        width: 640,
        height: 360,
        bitrate: 1_000_000,
        framerate: 30,
      });
      return !!res.supported;
    } catch {
      return false;
    }
  }

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly opts: WebCodecsRecorderOptions = {},
  ) {
    this.codecKey = opts.codec ?? 'vp9';
    const fps = opts.fps ?? 60;
    this.keyframeEvery = opts.keyFrameInterval ?? fps * 2;
  }

  isRecording(): boolean {
    return this.running;
  }

  elapsedMs(): number {
    return this.running ? performance.now() - this.startedAt : 0;
  }

  /**
   * Returns the `performance.now()` value at which the first encoded frame
   * was captured — i.e. the real-world anchor for the recorded video's
   * `t = 0`. Returns null if no frame has been captured yet.
   */
  getFirstFrameWallclock(): number | null {
    return this.firstFrameWallclock;
  }

  /**
   * Number of frames that actually reached the encoder. Useful for
   * callers to detect degenerate recordings (e.g. a single frame, which
   * produces a ~1ms-long video that the viewer can't play).
   */
  getFrameCount(): number {
    return this.frameCount;
  }

  start(): void {
    if (this.running) return;

    // Canvas dimensions must be non-zero at start.
    this.width = this.canvas.width;
    this.height = this.canvas.height;
    if (this.width === 0 || this.height === 0) {
      throw new Error('canvas must have non-zero dimensions before start()');
    }

    const fps = this.opts.fps ?? 60;
    const bitrate = this.opts.bitsPerSecond ?? 6_000_000;

    this.muxer = new Muxer({
      target: new ArrayBufferTarget(),
      video: {
        codec: MATROSKA_CODECS[this.codecKey],
        width: this.width,
        height: this.height,
        frameRate: fps,
      },
      type: 'webm',
      firstTimestampBehavior: 'offset',
    });

    this.encoder = new VideoEncoder({
      output: (chunk, meta) => {
        this.muxer?.addVideoChunk(chunk, meta);
      },
      error: (err) => {
        console.error('[WebCodecsRecorder] encoder error', err);
      },
    });
    this.encoder.configure({
      codec: CODEC_STRINGS[this.codecKey],
      width: this.width,
      height: this.height,
      bitrate,
      framerate: fps,
      latencyMode: 'realtime',
    });

    this.running = true;
    this.startedAt = performance.now();
    this.frameCount = 0;
    this.firstFrameWallclock = null;
  }

  /**
   * Capture the current canvas state as a single VideoFrame and feed it
   * to the encoder. Call this once per actual video frame — typically
   * from a `requestVideoFrameCallback` loop tied to the source `<video>`.
   */
  captureFrame(): void {
    if (!this.running || !this.encoder) return;
    if (this.canvas.width !== this.width || this.canvas.height !== this.height) {
      // Canvas dimensions changed mid-recording; WebCodecs requires fixed
      // dimensions per encoder configuration. Drop this frame and warn.
      console.warn('[WebCodecsRecorder] canvas dimensions changed; frame dropped');
      return;
    }

    const wallclock = performance.now();
    if (this.firstFrameWallclock === null) {
      this.firstFrameWallclock = wallclock;
    }
    this.lastFrameWallclock = wallclock;
    const tMs = wallclock - this.startedAt;
    const timestampUs = Math.max(0, Math.round(tMs * 1000));
    const keyFrame = this.frameCount % this.keyframeEvery === 0;

    let frame: VideoFrame;
    try {
      frame = new VideoFrame(this.canvas, {
        timestamp: timestampUs,
      });
    } catch (err) {
      console.warn('[WebCodecsRecorder] VideoFrame construction failed', err);
      return;
    }
    try {
      this.encoder.encode(frame, { keyFrame });
    } finally {
      frame.close();
    }
    this.frameCount += 1;
  }

  async stop(): Promise<Blob | null> {
    if (!this.running || !this.encoder || !this.muxer) return null;
    this.running = false;
    try {
      await this.encoder.flush();
    } catch (err) {
      console.error('[WebCodecsRecorder] flush failed', err);
    }
    this.muxer.finalize();
    const target = this.muxer.target as ArrayBufferTarget;
    const buffer = target.buffer;
    this.encoder.close();
    this.encoder = null;
    this.muxer = null;

    let blob = new Blob([buffer], { type: 'video/webm' });
    // webm-muxer sometimes leaves the Duration field unset / infinite in the
    // resulting WebM. That breaks seek precision and makes `video.duration`
    // report `Infinity` in the viewer, which cascades into the skeleton /
    // playhead sync going haywire. Patch in the real duration computed
    // from our capture wallclock timestamps.
    if (this.firstFrameWallclock !== null && this.lastFrameWallclock !== null) {
      const durationMs = Math.max(1, this.lastFrameWallclock - this.firstFrameWallclock);
      try {
        blob = await fixWebmDuration(blob, durationMs, { logger: false });
      } catch (err) {
        console.warn('[WebCodecsRecorder] fixWebmDuration failed', err);
      }
    }
    return blob;
  }
}
